import React from 'react';
import { Stack, CircularProgress, Typography } from '@mui/material';
import { useSummaryFetch, WidgetLoadError } from './useSummaryFetch';
import { UserType } from '../../../interfaces/userType.enum';
import { DashboardService } from '../../../services/dashboard.service';
import { InventorySummary, StoreValue, TopItem, WeeklyMovement, ReorderRow } from '../../../interfaces/dashboard.interface';
import { WidgetDef, WidgetProps } from './types';
import StatTile from '../charts/StatTile';
import HBars from '../charts/HBars';
import StackedColumns from '../charts/StackedColumns';
import TableView from '../charts/TableView';
import { fmtMoney, CURRENCY_SYMBOL } from './format';

const BUSINESS_ROLES = [UserType.InstitutionAdmin, UserType.InstitutionManager, UserType.Accountant];

const loadInventory = (institutionId: string): Promise<InventorySummary> =>
    DashboardService.getInventorySummary(institutionId);

const InventoryWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadInventory, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.itemCount === 0 && d.stockValue === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No stock recorded yet — receive stock to see figures here.</Typography>;
    }
    return (
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {/* Stock value is a neutral balance, not an achievement: it was hard-coded 'good',
                which painted even a zero-value store success-green. */}
            <StatTile label="Stock value" unit={CURRENCY_SYMBOL} value={fmtMoney(d.stockValue)} />
            <StatTile label="Low stock" value={d.lowStockCount} tone={d.lowStockCount > 0 ? 'warning' : 'default'} />
            <StatTile label="Items" value={d.itemCount} />
        </Stack>
    );
};

const loadValueByStore = (institutionId: string): Promise<StoreValue[]> =>
    DashboardService.getInventoryValueByStore(institutionId);

const InventoryValueByStoreWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadValueByStore, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No stock recorded yet — receive stock to see value by store.</Typography>;
    }
    return (
        <HBars
            data={d.map(s => ({
                label: s.storeName,
                value: s.value,
                tooltip: [{ label: 'Value', value: fmtMoney(s.value) }],
            }))}
            vFmt={fmtMoney}
        />
    );
};

const loadTopItems = (institutionId: string): Promise<TopItem[]> =>
    DashboardService.getInventoryTopItems(institutionId);

const InventoryTopItemsWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadTopItems, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No stock recorded yet — receive stock to see top items.</Typography>;
    }
    return (
        <HBars
            data={d.map(i => ({
                label: i.itemName,
                value: i.value,
                tooltip: [
                    { label: 'Value', value: fmtMoney(i.value) },
                    { label: 'Qty', value: String(i.qty) },
                ],
            }))}
            vFmt={fmtMoney}
        />
    );
};

const loadMovement = (institutionId: string): Promise<WeeklyMovement[]> =>
    DashboardService.getInventoryMovement(institutionId);

const InventoryMovementWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadMovement, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No stock movement in the last 8 weeks.</Typography>;
    }
    // Weeks with zero in/out stay on the axis as zero-height stacks — the backend zero-fills
    // the window, and a quiet week is a real figure, not missing data.
    return (
        <StackedColumns
            data={d.map(w => ({ label: w.weekStart.slice(5), segments: [w.inQty, w.outQty] }))}
            seriesLabels={['Stock in', 'Stock out']}
        />
    );
};

const loadReorder = (institutionId: string): Promise<ReorderRow[]> =>
    DashboardService.getInventoryReorder(institutionId);

const InventoryReorderWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadReorder, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>Nothing at or below reorder level.</Typography>;
    }
    return (
        <TableView
            columns={['Item', 'Store', 'Available', 'Reorder level']}
            rows={d.map(r => [r.itemName, r.storeName, r.availableQty, r.reorderLevel ?? '—'])}
        />
    );
};

export const inventoryWidgets: WidgetDef[] = [
    { id: 'inventory.summary', module: 'inventory', title: 'Inventory — stock value & low stock', roles: BUSINESS_ROLES, size: 'md', component: InventoryWidget },
    { id: 'inventory.valueByStore', module: 'inventory', title: 'Inventory — value by store', roles: BUSINESS_ROLES, size: 'md', component: InventoryValueByStoreWidget },
    { id: 'inventory.topItems', module: 'inventory', title: 'Inventory — top items by value', roles: BUSINESS_ROLES, size: 'md', component: InventoryTopItemsWidget },
    { id: 'inventory.movement', module: 'inventory', title: 'Inventory — stock movement', roles: BUSINESS_ROLES, size: 'lg', component: InventoryMovementWidget },
    { id: 'inventory.reorder', module: 'inventory', title: 'Inventory — reorder list', roles: BUSINESS_ROLES, size: 'lg', component: InventoryReorderWidget },
];
